import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";

const W = 1600, H = 1150, CX = 800, CY = 530, K = 0.56, GROUND = 760, TRUNK_TOP = 520;
const rad = (d) => (d * Math.PI) / 180;
const pt = (r, deg) => [CX + r * Math.cos(rad(deg)), CY - r * K * Math.sin(rad(deg))];
const rootPt = (r, deg) => [CX + r * Math.cos(rad(deg)), GROUND - r * 0.62 * Math.sin(rad(deg))];
const LEAF_R = { consolidato: 9, parziale: 7, da_approfondire: 5.5 };
const MAT_LABEL = { consolidato: "consolidato", parziale: "parziale", da_approfondire: "da approfondire" };
const trunc = (s, n) => (s.length > n ? s.slice(0, n - 1) + "…" : s);
const twoLines = (s) => { if (s.length <= 18) return [s]; const i = s.lastIndexOf(" ", Math.ceil(s.length / 2) + 4); return i > 0 ? [s.slice(0, i), s.slice(i + 1)] : [s]; };

const RadialLabel = ({ r, deg, text, size = 10.5, fill, bold, testid }) => {
  const [x, y] = pt(r, deg);
  const left = Math.cos(rad(deg)) < 0;
  const rot = -Math.atan2(K * Math.sin(rad(deg)), Math.cos(rad(deg))) * 180 / Math.PI;
  return <text data-testid={testid} x={x} y={y} fontSize={size} fill={fill} fontWeight={bold ? 600 : 400} textAnchor={left ? "end" : "start"} dominantBaseline="middle"
    stroke="#15110D" strokeWidth="3" paintOrder="stroke" transform={`rotate(${left ? rot + 180 : rot} ${x} ${y})`}>{text}</text>;
};

export const TreeMap = ({ tree, selected, selectedFile, onSelect, onSelectFile, maturity }) => {
  const [files, setFiles] = useState([]);
  const [hover, setHover] = useState(null);
  useEffect(() => { api.get("/files").then((r) => setFiles(r.data)); }, [tree]);

  const sector = 176 / Math.max(tree.length, 1);
  const dim = (m) => maturity && m !== maturity;

  return (
    <div data-testid="tree-map" className="surface p-3 overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[900px] h-auto" role="img" aria-label={t("Mappa dell'albero decisionale")}>
        <defs>
          <linearGradient id="trunk" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#7A563C" /><stop offset="1" stopColor="#3E2C1F" /></linearGradient>
          <radialGradient id="glow" cx="50%" cy="50%" r="50%"><stop offset="0" stopColor="#E67E22" stopOpacity="0.16" /><stop offset="1" stopColor="#E67E22" stopOpacity="0" /></radialGradient>
          <radialGradient id="soil" cx="50%" cy="0%" r="100%"><stop offset="0" stopColor="#2A2119" stopOpacity="0.9" /><stop offset="1" stopColor="#15110D" stopOpacity="0" /></radialGradient>
        </defs>

        <ellipse cx={CX} cy={CY - 60} rx={520} ry={300} fill="url(#glow)" pointerEvents="none" />
        <rect x="0" y={GROUND} width={W} height={H - GROUND} fill="url(#soil)" pointerEvents="none" />
        <ellipse cx={CX} cy={GROUND} rx={560} ry={12} fill="#3A2E24" pointerEvents="none" />
        <text x={CX} y={GROUND + 30} fontSize="11" fill="#8C8479" textAnchor="middle" letterSpacing="2">{t("RADICI · DISPENSE E DOCUMENTI DEL CORPUS")}</text>

        {files.map((f, i) => {
          const n = files.length;
          const deg = 192 + ((i + 0.5) * 156) / n;
          const r = [300, 400, 500][i % 3];
          const [x, y] = rootPt(r, deg);
          const [cx2, cy2] = rootPt(r * 0.45, deg);
          const ok = f.status === "assimilato";
          const cosd = Math.cos(rad(deg));
          const lateral = Math.abs(cosd) > 0.45;
          const hot = hover === `f:${f.id}` || selectedFile === f.id;
          return (
            <g key={f.id} data-testid={`map-root-${f.id}`} className="cursor-pointer" onClick={() => onSelectFile(f.id)}
              onMouseEnter={() => setHover(`f:${f.id}`)} onMouseLeave={() => setHover(null)}>
              <title>{f.name} · {f.kind} · {ok ? "assimilato" : f.status}</title>
              <path d={`M ${CX} ${GROUND - 2} Q ${cx2} ${cy2 + 40} ${x} ${y}`} fill="none" stroke={hot ? "#E0B98A" : "#6B4F3A"} strokeWidth={hot ? 4 : i % 2 ? 2.5 : 3.5} strokeLinecap="round" />
              <circle cx={x} cy={y} r={16} fill="transparent" />
              <circle cx={x} cy={y} r={hot ? 7 : ok ? 5 : 4} fill={ok ? "#B8895A" : "#15110D"} stroke={selectedFile === f.id ? "#FFFFFF" : "#B8895A"} strokeWidth={selectedFile === f.id ? 2.5 : 1.5} />
              <text x={lateral ? x + (cosd < 0 ? -10 : 10) : x} y={lateral ? y : y + 16} fontSize="10.5" fill={hot ? "#F5EFE6" : "#A08A72"} fontWeight={selectedFile === f.id ? 600 : 400} textAnchor={lateral ? (cosd < 0 ? "end" : "start") : "middle"} dominantBaseline="middle"
                stroke="#15110D" strokeWidth="3" paintOrder="stroke">{trunc(f.name, 26)}</text>
            </g>
          );
        })}

        <path d={`M ${CX - 48} ${GROUND + 4} C ${CX - 42} ${GROUND - 90} ${CX - 26} ${GROUND - 130} ${CX - 24} ${TRUNK_TOP + 40} Q ${CX} ${TRUNK_TOP + 10} ${CX + 24} ${TRUNK_TOP + 40} C ${CX + 26} ${GROUND - 130} ${CX + 42} ${GROUND - 90} ${CX + 48} ${GROUND + 4} Z`} fill="url(#trunk)" />

        {tree.map((b, i) => {
          const deg = 178 - sector * (i + 0.5);
          const [bx, by] = pt(430, deg);
          const cosd = Math.cos(rad(deg));
          const ax = CX + 22 * cosd, ay = TRUNK_TOP + 6 + 70 * Math.abs(cosd);
          const [c1x, c1y] = pt(190, deg + (deg > 90 ? 12 : -12));
          const active = selected === b.id || selected?.startsWith(b.id + ".");
          const kids = b.children || [];
          const span = sector * 0.82;
          const dx = bx - ax, dy = by - ay, len = Math.hypot(dx, dy) || 1;
          const side = Math.abs(cosd) < 0.35;
          const tx = side ? bx + (cosd < 0 ? -34 : 34) : bx, ty = side ? by + 34 : by + 44;
          const anchor = side ? (cosd < 0 ? "end" : "start") : "middle";
          return (
            <g key={b.id}>
              <path d={`M ${ax} ${ay} Q ${c1x} ${c1y} ${bx} ${by}`} fill="none" stroke={b.color} strokeOpacity={active ? 1 : 0.75} strokeWidth={active ? 11 : 9} strokeLinecap="round" />
              {kids.map((c, j) => {
                const cdeg = deg + span / 2 - (span * (j + 0.5)) / kids.length;
                const r = j % 2 ? 640 : 560;
                const [lx, ly] = pt(r, cdeg);
                const isSel = selected === c.id;
                const faded = dim(c.maturity);
                const size = LEAF_R[c.maturity] || 6;
                return (
                  <g key={c.id} data-testid={`map-leaf-${c.id}`} className="cursor-pointer" opacity={faded ? 0.18 : 1} onClick={() => onSelect(c.id)}
                    onMouseEnter={() => setHover(c.id)} onMouseLeave={() => setHover(null)}>
                    <title>{c.code} {c.title} · {MAT_LABEL[c.maturity]} · {(c.children || []).length} concetti</title>
                    <path d={`M ${bx} ${by} Q ${pt(500, (deg + cdeg) / 2)[0]} ${pt(500, (deg + cdeg) / 2)[1]} ${lx} ${ly}`} fill="none" stroke={b.color} strokeOpacity={0.55} strokeWidth={2} />
                    <circle cx={lx} cy={ly} r={16} fill="transparent" />
                    <circle cx={lx} cy={ly} r={hover === c.id || isSel ? size + 3 : size} fill={c.maturity === "da_approfondire" ? "#15110D" : b.color} fillOpacity={c.maturity === "parziale" ? 0.6 : 1} stroke={isSel ? "#FFFFFF" : b.color} strokeWidth={isSel ? 2.5 : 1.5} />
                    <RadialLabel r={r + size + 8} deg={cdeg} text={trunc(c.title, 26)} fill={isSel || hover === c.id ? "#F5EFE6" : "#BDB4A8"} bold={isSel} />
                  </g>
                );
              })}
              <g data-testid={`map-branch-${b.id}`} className="cursor-pointer" onClick={() => onSelect(b.id)} onMouseEnter={() => setHover(b.id)} onMouseLeave={() => setHover(null)}>
                <title>{b.title} · {kids.length} sotto-rami · {MAT_LABEL[b.maturity]}</title>
                <circle cx={bx} cy={by} r={hover === b.id || active ? 25 : 22} fill="#15110D" stroke={b.color} strokeWidth={active ? 4 : 3} />
                <text x={bx} y={by + 1} fontSize="15" fontWeight="700" fill={b.color} textAnchor="middle" dominantBaseline="middle" style={{ pointerEvents: "none" }}>{b.code}</text>
                {twoLines(b.title).map((line, k, arr) => (
                  <text key={k} x={tx} y={ty + (k - (arr.length - 1) / 2) * 14} fontSize="12" fontWeight="600" fill={active ? "#F5EFE6" : b.color} textAnchor={anchor} dominantBaseline="middle"
                    stroke="#15110D" strokeWidth="4" paintOrder="stroke" style={{ pointerEvents: "none" }}>{line}</text>
                ))}
              </g>
            </g>
          );
        })}

        <g transform={`translate(28 ${H - 96})`} fontSize="11" fill="#8C8479">
          <text y="0" fontSize="10" letterSpacing="2">{t("LEGENDA")}</text>
          <circle cx="8" cy="22" r="9" fill="#E67E22" /><text x="24" y="26">{t("Foglia piena grande · nodo consolidato")}</text>
          <circle cx="8" cy="46" r="7" fill="#E67E22" fillOpacity="0.6" /><text x="24" y="50">{t("Foglia media · nodo parziale")}</text>
          <circle cx="8" cy="70" r="5.5" fill="#15110D" stroke="#E67E22" strokeWidth="1.5" /><text x="24" y="74">{t("Foglia vuota piccola · da approfondire")}</text>
        </g>
        <text x={W - 28} y={H - 30} fontSize="11" fill="#8C8479" textAnchor="end">{t("Tocca un ramo, una foglia o una radice per aprire la scheda")}</text>
      </svg>
    </div>
  );
};
